import React, { FC, useMemo } from 'react';
import type { StylesConfig } from 'react-select';

import SearchableSelect, {
    SearchableSelectType,
} from 'components/inputs/searchableSelect';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';
import routes from '../../../../constants/routes';

import { FormulaBaseModel } from '../../../../models/formula.model';

import MapElementList from '../MapElementList';

const customStyles: StylesConfig<SearchableSelectType> = {
    container: (provided) => ({
        ...provided,
        width: '100%',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: aampeTheme.colors.gray700,
    }),
    option: (provided, state) => ({
        ...provided,
        cursor: 'pointer',
        backgroundColor:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.backgroundWG01
                : aampeTheme.colors.white,
        color:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.gray700
                : aampeTheme.colors.neutral09,
        padding: aampeTheme.spacing(1, 2),
    }),
};

interface Step2FieldMappingProps {
    selectedMessages: FormulaBaseModel[];
    selectedMessageForPreview: FormulaBaseModel;
    setSelectedMessageForPreview: (message: FormulaBaseModel) => void;
    mapOptions: SearchableSelectType[];
    showMappingErrors?: boolean;
}

const Step2FieldMapping: FC<Step2FieldMappingProps> = ({
    selectedMessages,
    selectedMessageForPreview,
    setSelectedMessageForPreview,
    mapOptions,
    showMappingErrors,
}) => {
    const messageOptions = useMemo<SearchableSelectType[]>(() => {
        return selectedMessages.map((msg) => ({
            label: msg.name || 'Untitled Message',
            value: msg.id.toString(),
        }));
    }, [selectedMessages]);

    const selectedMessageOption = useMemo<
        SearchableSelectType | undefined
    >(() => {
        return messageOptions.find(
            (opt) => opt.value === selectedMessageForPreview.id.toString(),
        );
    }, [messageOptions, selectedMessageForPreview]);

    const handleMessageChange = (option: SearchableSelectType) => {
        const message = selectedMessages.find(
            (msg) => msg.id.toString() === option.value,
        );
        if (message) {
            setSelectedMessageForPreview(message);
        }
    };

    const messageUrl = useMemo(() => {
        return routes.formulaView(
            selectedMessageForPreview.status === 'Approved'
                ? 'Live'
                : selectedMessageForPreview.status,
            selectedMessageForPreview.id,
        );
    }, [selectedMessageForPreview]);

    return (
        <Container>
            <TopSection>
                <TopRow>
                    <InfoSection>
                        <InfoRow>
                            <Label>
                                Changing the email template will affect{' '}
                                <Count>
                                    {selectedMessages.length} message group
                                    {selectedMessages.length !== 1 ? 's' : ''}
                                </Count>
                                .
                            </Label>
                        </InfoRow>
                        <InfoText>
                            Assign the elements below to the corresponding
                            fields in the new template.
                        </InfoText>
                    </InfoSection>

                    <PreviewSection>
                        <Label>Currently previewing message content from</Label>
                        <MessageSelectorRow>
                            <MessageDropdown>
                                <SearchableSelect
                                    name="previewMessage"
                                    options={messageOptions}
                                    onChange={(field, value) => {
                                        handleMessageChange(
                                            value as SearchableSelectType,
                                        );
                                    }}
                                    placeholder="Select message"
                                    customStyles={customStyles}
                                    value={selectedMessageOption}
                                />
                            </MessageDropdown>
                            <IconButton
                                onClick={() =>
                                    window.open(messageUrl, '_blank')
                                }
                                aria-label="Open message in new tab"
                            >
                                <ICONS.externalLink size={18} />
                            </IconButton>
                        </MessageSelectorRow>
                    </PreviewSection>
                </TopRow>
            </TopSection>

            <MappingSection>
                <MapElementList
                    mapOptions={mapOptions}
                    selectedMessageForPreview={selectedMessageForPreview}
                    showMappingErrors={showMappingErrors}
                />
            </MappingSection>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    height: '100%',
});

const TopSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const TopRow = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    gap: aampeTheme.spacing4(10),
    padding: aampeTheme.spacing4(3, 4, 4, 4),
});

const InfoSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    flex: 1,
});

const InfoRow = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const Label = styled.span({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.heavy,
    lineHeight: aampeTheme.size.lineHeight.small,
});

const Count = styled.span({
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
});

const InfoText = styled.span({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
});

const PreviewSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    flex: 1,
    paddingLeft: aampeTheme.spacing4(4),
});

const MessageSelectorRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const MessageDropdown = styled.div({
    minWidth: '280px',
    flexGrow: 1,
});

const IconButton = styled.button({
    background: 'none',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    cursor: 'pointer',
    padding: aampeTheme.spacing4(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: aampeTheme.colors.solidA01,
    transition: 'all 0.2s',
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG02,
        borderColor: aampeTheme.colors.solidA01,
    },
});

const MappingSection = styled.div({
    flex: 1,
    overflow: 'auto',
});

export default Step2FieldMapping;
